import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Grid, Typography, CircularProgress,
} from '@mui/material';
import {
  People, Inventory, Receipt, Assessment, Settings, Add, CurrencyRupee,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { User } from '@/models/user';
import { ReportsService } from '@/services/reportsService';
import { StatsResponse } from '@/models/common';
import { StatCard, DashboardCard } from '@/components';
import './HomePage.css';

interface HomePageProps {
  user: User;
  onLogout: () => void;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

const STAT_COLORS = {
  invoices: '#3b82f6',
  customers: '#10b981',
  products: '#f59e0b',
  revenue: '#8b5cf6',
};

const DASHBOARD_CARDS = [
  { id: 'create-invoice', title: 'Create Invoice', description: 'Generate new invoices', icon: <Add />, color: '#3b82f6', route: '/create-invoice', gradient: '' },
  { id: 'customers', title: 'Customer Management', description: 'Manage customer records', icon: <People />, color: '#10b981', route: '/customers', gradient: '' },
  { id: 'products', title: 'Product Management', description: 'Manage product catalog', icon: <Inventory />, color: '#f59e0b', route: '/products', gradient: '' },
  { id: 'invoices', title: 'Invoice Management', description: 'View and manage invoices', icon: <Receipt />, color: '#8b5cf6', route: '/invoices', gradient: '' },
  { id: 'reports', title: 'Reports & Analytics', description: 'Sales and HSN reports', icon: <Assessment />, color: '#ef4444', route: '/reports', gradient: '' },
  { id: 'settings', title: 'Settings', description: 'Configure application', icon: <Settings />, color: '#0ea5e9', route: '/settings', gradient: '' },
];

function getGreeting(name: string): string {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  return `${greeting}, ${name}`;
}

function formatCurrency(amount: number): string {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(2)}K`;
  return `₹${amount.toFixed(2)}`;
}

function formatDate(): string {
  return new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

const HomePage: React.FC<HomePageProps> = ({ user, darkMode }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await ReportsService.getStats();
        if (!response) { setError('No response received'); return; }
        if ('success' in response) {
          if (response.success && response.data) setStats(response.data);
          else setError(response.message || 'Failed to load statistics');
        } else if ('totalBillsFY' in response || 'totalBillsMonth' in response) {
          setStats(response as StatsResponse);
        } else if ('data' in response && response.data) {
          setStats(response.data as StatsResponse);
        } else {
          setError('Invalid response format');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load statistics';
        if (message.includes('cancelled') || message.includes('Request cancelled')) return;
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { id: 'invoices-month', title: 'Invoices This Month', value: stats ? stats.totalBillsMonth.toString() : '—', change: 0, icon: <Receipt />, color: STAT_COLORS.invoices, gradient: '' },
    { id: 'total-customers', title: 'Total Customers', value: stats ? stats.totalCustomers.toString() : '—', change: 0, icon: <People />, color: STAT_COLORS.customers, gradient: '' },
    { id: 'products', title: 'Products', value: stats ? stats.totalProducts.toString() : '—', change: 0, icon: <Inventory />, color: STAT_COLORS.products, gradient: '' },
    { id: 'monthly-revenue', title: 'Monthly Revenue', value: stats ? formatCurrency(stats.monthlyRevenue) : '—', change: 0, icon: <CurrencyRupee />, color: STAT_COLORS.revenue, gradient: '' },
  ];

  const revenueData = stats ? [
    { name: 'This Month', value: stats.monthlyRevenue, color: STAT_COLORS.invoices },
    { name: 'FY Total', value: stats.fyRevenue, color: STAT_COLORS.revenue },
  ] : [];

  const tickFormatter = (val: number) => formatCurrency(val);

  return (
    <Box className={`home-page ${darkMode ? 'dark' : 'light'}`}>
      <Container maxWidth="xl" className="home-container">

        {/* Welcome banner */}
        <Box className="welcome-banner">
          <Box>
            <Typography variant="h5" className="welcome-greeting">
              {getGreeting(user.username || 'there')}
            </Typography>
            <Typography variant="body2" className="welcome-date">
              {formatDate()} · Here&apos;s your business overview
            </Typography>
          </Box>
        </Box>

        {/* Stats */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        ) : (
          <Grid container spacing={2} className="stats-grid">
            {statCards.map((stat) => (
              <Grid item xs={12} sm={6} md={3} key={stat.id}>
                <StatCard {...stat} darkMode={darkMode} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Revenue chart */}
        {stats && (
          <Box className={`revenue-section ${darkMode ? 'dark' : 'light'}`}>
            <Typography variant="subtitle1" className="section-heading">Revenue Overview</Typography>
            <Typography variant="caption" className="section-sub">Month vs Fiscal Year comparison</Typography>
            <Box className="chart-wrap">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={revenueData} barSize={48} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: darkMode ? '#9ca3af' : '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={tickFormatter}
                    tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  <ChartTooltip
                    formatter={(val: number) => [formatCurrency(val), 'Revenue']}
                    contentStyle={{
                      borderRadius: 8,
                      border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                      background: darkMode ? '#1f2937' : '#fff',
                      color: darkMode ? '#f9fafb' : '#0f172a',
                      fontSize: 12,
                    }}
                    cursor={{ fill: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {revenueData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {/* Quick actions */}
        <Box className="quick-actions-section">
          <Typography variant="subtitle1" className="section-heading">Quick Actions</Typography>
          <Typography variant="caption" className="section-sub">Access your most important tools</Typography>
          <Grid container spacing={1.5} className="dashboard-grid">
            {DASHBOARD_CARDS.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.id}>
                <DashboardCard
                  {...card}
                  darkMode={darkMode}
                  onClick={(route) => navigate(route)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

      </Container>
    </Box>
  );
};

export default HomePage;
